import React, {useEffect, useState} from 'react'
import {toast} from 'react-toastify'
import {showErrorToast} from '../../../../shared/utils/apiErrorMessage'
import {CrewFlagDocument, FlagDocType} from '../../core/_models'
import {
  saveFlagDocument,
  deleteFlagDocument,
  deleteFlagDocumentFile,
  fetchFlagDocumentBlob,
  getOnboardingToken,
} from '../../core/_requests'
import {useAuth} from '../../../auth'

interface FlagDocumentsProps {
  crewId: number | null
  fieldErrors: Record<string, string>
  markSaved: () => void
  setIsDirty: (dirty: boolean) => void
  existingFlagDocuments?: CrewFlagDocument[] | null
}

interface FlagRow {
  flag: string
  docType: FlagDocType
  documentNumber: string
  issueDate: string // yyyy-MM-dd
  expiryDate: string
  id: number | null
  filePath: string | null
  fileName: string | null
  file?: File | null
}

export const DEFAULT_FLAGS: string[] = [
  'GAMBIA',
  'COMOROS',
  'MOZAMBIQUE',
  'MARSHALL_ISLANDS',
  'COOK_ISLANDS',
]

export const POPULAR_FLAGS: {code: string; label: string}[] = [
  {code: 'PANAMA', label: 'PANAMA'},
  {code: 'LIBERIA', label: 'LIBERIA'},
  {code: 'MARSHALL_ISLANDS', label: 'MARSHALL ISLANDS'},
  {code: 'SINGAPORE', label: 'SINGAPORE'},
  {code: 'MALTA', label: 'MALTA'},
  {code: 'BAHAMAS', label: 'BAHAMAS'},
  {code: 'CYPRUS', label: 'CYPRUS'},
  {code: 'ISLE_OF_MAN', label: 'ISLE OF MAN'},
  {code: 'HONG_KONG', label: 'HONG KONG'},
  {code: 'BELIZE', label: 'BELIZE'},
  {code: 'ST_VINCENT', label: 'ST. VINCENT & THE GRENADINES'},
  {code: 'ANTIGUA_AND_BARBUDA', label: 'ANTIGUA & BARBUDA'},
  {code: 'VANUATU', label: 'VANUATU'},
  {code: 'PALAU', label: 'PALAU'},
  {code: 'BERMUDA', label: 'BERMUDA'},
  {code: 'CAYMAN_ISLANDS', label: 'CAYMAN ISLANDS'},
  {code: 'GIBRALTAR', label: 'GIBRALTAR'},
  {code: 'GABON', label: 'GABON'},
  {code: 'TOGO', label: 'TOGO'},
  {code: 'HONDURAS', label: 'HONDURAS'},
  {code: 'TUVALU', label: 'TUVALU'},
  {code: 'COOK_ISLANDS', label: 'COOK ISLANDS'},
  {code: 'GAMBIA', label: 'GAMBIA'},
  {code: 'COMOROS', label: 'COMOROS'},
  {code: 'MOZAMBIQUE', label: 'MOZAMBIQUE'},
  {code: 'INDIA', label: 'INDIA'},
  {code: 'UNITED_KINGDOM', label: 'UNITED KINGDOM'},
  {code: 'UNITED_STATES', label: 'UNITED STATES'},
]

export const FLAG_LABELS: Record<string, string> = {
  GAMBIA: 'GAMBIA',
  COMOROS: 'COMOROS',
  MOZAMBIQUE: 'MOZAMBIQUE',
  MARSHALL_ISLANDS: 'MARSHALL ISLANDS',
  COOK_ISLANDS: 'COOK ISLANDS',
  PANAMA: 'PANAMA',
  LIBERIA: 'LIBERIA',
  SINGAPORE: 'SINGAPORE',
  MALTA: 'MALTA',
  BAHAMAS: 'BAHAMAS',
  CYPRUS: 'CYPRUS',
  ISLE_OF_MAN: 'ISLE OF MAN',
  HONG_KONG: 'HONG KONG',
  BELIZE: 'BELIZE',
  ST_VINCENT: 'ST. VINCENT & THE GRENADINES',
  ANTIGUA_AND_BARBUDA: 'ANTIGUA & BARBUDA',
  VANUATU: 'VANUATU',
  PALAU: 'PALAU',
  BERMUDA: 'BERMUDA',
  CAYMAN_ISLANDS: 'CAYMAN ISLANDS',
  GIBRALTAR: 'GIBRALTAR',
  GABON: 'GABON',
  TOGO: 'TOGO',
  HONDURAS: 'HONDURAS',
  TUVALU: 'TUVALU',
  INDIA: 'INDIA',
  UNITED_KINGDOM: 'UNITED KINGDOM',
  UNITED_STATES: 'UNITED STATES',
}

export const getFlagLabel = (flag: string): string => {
  if (FLAG_LABELS[flag]) return FLAG_LABELS[flag]
  return flag.replace(/_/g, ' ').toUpperCase()
}

export const DOC_TYPES: FlagDocType[] = [
  'COC',
  'GMDSS',
  'SSO',
  'OIL_ENDORSEMENT',
  'CHEM_ENDORSEMENT',
  'STSDSD',
  'CRA',
]

export const DOC_LABELS: Record<FlagDocType, string> = {
  COC: 'COC',
  GMDSS: 'GMDSS',
  SSO: 'SSO',
  OIL_ENDORSEMENT: 'OIL ENDORSEMENT',
  CHEM_ENDORSEMENT: 'CHEM ENDORSEMENT',
  STSDSD: 'STSDSD',
  CRA: 'CRA',
}

const makeInitialRows = (flags: string[] = DEFAULT_FLAGS): FlagRow[] => {
  const rows: FlagRow[] = []
  for (const flag of flags) {
    for (const docType of DOC_TYPES) {
      rows.push({
        flag,
        docType,
        documentNumber: '',
        issueDate: '',
        expiryDate: '',
        id: null,
        filePath: null,
        fileName: null,
        file: null,
      })
    }
  }
  return rows
}

const parseDateToString = (val: any): string => {
  if (!val) return ''
  if (typeof val === 'string') {
    if (val.includes('T')) {
      return val.split('T')[0]
    }
    return val
  }
  if (Array.isArray(val)) {
    if (val.length >= 3) {
      const y = val[0]
      const m = String(val[1]).padStart(2, '0')
      const d = String(val[2]).padStart(2, '0')
      return `${y}-${m}-${d}`
    }
    return ''
  }
  if (typeof val === 'object') {
    const y = val.year
    const m = val.monthValue ?? val.month
    const d = val.dayOfMonth ?? val.day
    if (y !== undefined && m !== undefined && d !== undefined) {
      return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`
    }
  }
  return ''
}

const FlagDocuments = React.forwardRef<{saveAll: () => Promise<boolean>}, FlagDocumentsProps>(
  ({crewId, fieldErrors, markSaved, setIsDirty, existingFlagDocuments}, ref) => {
    const {isSuperAdmin, hasPermission} = useAuth()
    const isOnboarding = !!getOnboardingToken()
    const canUploadCrewDocument =
      isSuperAdmin || hasPermission('UPLOAD_CREW_DOCUMENT') || isOnboarding
    const canDeleteCrewDocument =
      isSuperAdmin || hasPermission('DELETE_CREW_DOCUMENT') || isOnboarding
    const canViewCrewDocumentFile =
      isSuperAdmin || hasPermission('READ_CREW_DOCUMENT') || isOnboarding

    const [activeFlags, setActiveFlags] = useState<string[]>(DEFAULT_FLAGS)
    const [rows, setRows] = useState<FlagRow[]>(() => makeInitialRows(DEFAULT_FLAGS))
    const [initialRows, setInitialRows] = useState<FlagRow[]>(() => makeInitialRows(DEFAULT_FLAGS))

    const [selectedNewFlag, setSelectedNewFlag] = useState<string>('')
    const [customFlagInput, setCustomFlagInput] = useState<string>('')
    const [showCustomInput, setShowCustomInput] = useState<boolean>(false)

    const isRowDirty = (index: number): boolean => {
      const r = rows[index]
      const init = initialRows[index]
      if (!r || !init) return false
      return (
        r.documentNumber !== init.documentNumber ||
        r.issueDate !== init.issueDate ||
        r.expiryDate !== init.expiryDate ||
        !!r.file
      )
    }

    const [objectUrl, setObjectUrl] = useState<string | null>(null)
    const [viewer, setViewer] = useState<{
      open: boolean
      href: string | null
      title: string
      mime?: string
    }>({open: false, href: null, title: ''})

    const closeViewer = () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl)
        setObjectUrl(null)
      }
      setViewer({open: false, href: null, title: '', mime: undefined})
    }

    React.useImperativeHandle(ref, () => ({
      saveAll: async () => {
        if (!crewId && !isOnboarding) return false
        const dirtyIndices = rows.map((_, i) => i).filter(isRowDirty)
        if (!dirtyIndices.length) return true // Nothing to save
        try {
          const results = await Promise.all(dirtyIndices.map(handleSaveRow))
          const allSucceeded = results.every(Boolean)
          if (!allSucceeded) {
            return false
          }
          markSaved()
          setIsDirty(false)
          setInitialRows([...rows]) // Update snapshot after save
          return true
        } catch (err) {
          showErrorToast(err, 'Failed to save all flag documents')
          return false
        }
      },
    }))

    const validateFile = (file: File | null) => {
      const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf']
      const maxSize = 9 * 1024 * 1024
      if (!file) return {isValid: true, message: ''}
      if (!validTypes.includes(file.type)) {
        return {
          isValid: false,
          message: 'Invalid file type. Only JPEG, PNG, JPG, and PDF are allowed.',
        }
      }
      if (file.size > maxSize) {
        return {
          isValid: false,
          message: 'File is too large. Maximum size is 9MB.',
        }
      }
      return {isValid: true, message: ''}
    }

    const updateRow = (index: number, patch: Partial<FlagRow>) => {
      setRows((prev) => {
        const copy = [...prev]
        copy[index] = {...copy[index], ...patch}
        return copy
      })
      setIsDirty(true)
    }

    const handleFieldChange = (
      index: number,
      field: keyof Pick<FlagRow, 'documentNumber' | 'issueDate' | 'expiryDate'>,
      value: string
    ) => {
      updateRow(index, {[field]: value})
    }

    const handleFileChange = (index: number, file: File | null) => {
      const validation = validateFile(file)
      if (!validation.isValid) {
        toast.warn(validation.message)
        return
      }
      updateRow(index, {file})
    }

    const handleSaveRow = async (index: number): Promise<boolean> => {
      if (!crewId && !isOnboarding) {
        toast.warn('Please create/save Personal Data (crew) first.')
        return false
      }
      const row = rows[index]
      try {
        const res = await saveFlagDocument({
          crewId,
          flag: row.flag,
          docType: row.docType,
          documentNumber: row.documentNumber || '',
          issueDate: row.issueDate || '',
          expiryDate: row.expiryDate || '',
          file: row.file ?? null,
        })
        setRows((prev) => {
          const copy = [...prev]
          copy[index] = {
            ...copy[index],
            id: res.id,
            documentNumber: res.documentNumber ?? '',
            issueDate: parseDateToString(res.issueDate),
            expiryDate: parseDateToString(res.expiryDate),
            filePath: res.filePath ?? null,
            fileName: res.fileName ?? null,
            file: null,
          }
          return copy
        })
        setIsDirty(false)
        markSaved()
        toast.success(`${getFlagLabel(row.flag)} - ${DOC_LABELS[row.docType]} saved.`)
        return true
      } catch (err) {
        console.error('Failed to save flag document', err)
        showErrorToast(err, 'Failed to save flag document')
        return false
      }
    }

    const handlePreview = async (index: number) => {
      const row = rows[index]
      if ((!crewId && !isOnboarding) || !row.id) {
        toast.warn('Nothing to preview for this document.')
        return
      }
      try {
        const blob = await fetchFlagDocumentBlob(crewId, row.id)
        const url = URL.createObjectURL(blob)
        setObjectUrl(url)
        setViewer({
          open: true,
          href: url,
          title: `${getFlagLabel(row.flag)} - ${DOC_LABELS[row.docType]}`,
          mime: blob.type || undefined,
        })
      } catch (err) {
        console.error('Failed to preview flag document', err)
        showErrorToast(err, 'Failed to preview flag document')
      }
    }

    const handleRemoveFile = async (index: number) => {
      const row = rows[index]
      if ((!crewId && !isOnboarding) || !row.id) {
        // just clear local file selection
        updateRow(index, {file: null, filePath: null, fileName: null})
        return
      }
      try {
        await deleteFlagDocumentFile(crewId, row.id)
        toast.success(`${getFlagLabel(row.flag)} - ${DOC_LABELS[row.docType]} file removed.`)
        setRows((prev) => {
          const copy = [...prev]
          copy[index] = {
            ...copy[index],
            filePath: null,
            fileName: null,
            file: null,
          }
          return copy
        })
      } catch (err) {
        console.error('Failed to delete flag document file', err)
        showErrorToast(err, 'Failed to delete flag document file')
      }
    }

    const handleAddFlag = (flagToAdd?: string) => {
      const rawInput = flagToAdd || (showCustomInput ? customFlagInput : selectedNewFlag)
      const flagName = (rawInput || '')
        .trim()
        .toUpperCase()
        .replace(/[\s-]+/g, '_')
        .replace(/[^A-Z0-9_]/g, '')

      if (!flagName) {
        toast.warn('Please select or enter a Flag Country.')
        return
      }

      if (activeFlags.includes(flagName)) {
        toast.warn(`Flag ${getFlagLabel(flagName)} is already present.`)
        return
      }

      const updatedFlags = [...activeFlags, flagName]
      setActiveFlags(updatedFlags)

      // Generate 7 rows for this new flag
      const newDocRows: FlagRow[] = DOC_TYPES.map((docType) => ({
        flag: flagName,
        docType,
        documentNumber: '',
        issueDate: '',
        expiryDate: '',
        id: null,
        filePath: null,
        fileName: null,
        file: null,
      }))

      setRows((prev) => [...prev, ...newDocRows])
      setInitialRows((prev) => [...prev, ...newDocRows])
      setSelectedNewFlag('')
      setCustomFlagInput('')
      setShowCustomInput(false)
      setIsDirty(true)
      toast.success(`Flag ${getFlagLabel(flagName)} added with 7 standard document upload rows.`)
    }

    const handleRemoveFlag = async (flagToRemove: string) => {
      const rowsForFlag = rows.filter((r) => r.flag === flagToRemove)
      const savedRows = rowsForFlag.filter((r) => r.id)

      if (savedRows.length > 0 && crewId) {
        const confirmDelete = window.confirm(
          `Flag ${getFlagLabel(flagToRemove)} has ${savedRows.length} saved document(s). Are you sure you want to delete this flag and its documents?`
        )
        if (!confirmDelete) return

        try {
          for (const r of savedRows) {
            if (r.id) {
              await deleteFlagDocument(crewId, r.id)
            }
          }
        } catch (err) {
          console.error('Failed to delete some flag documents from server', err)
          showErrorToast(err, 'Failed to delete some flag documents from server')
        }
      }

      setActiveFlags((prev) => prev.filter((f) => f !== flagToRemove))
      setRows((prev) => prev.filter((r) => r.flag !== flagToRemove))
      setInitialRows((prev) => prev.filter((r) => r.flag !== flagToRemove))
      setIsDirty(true)
      toast.info(`Flag ${getFlagLabel(flagToRemove)} removed.`)
    }

    const renderFileCell = (index: number) => {
      const row = rows[index]
      const hasNewFile = !!row.file

      // Case: file already exists on server
      if (row.filePath) {
        return (
          <div className='d-flex align-items-center gap-2 flex-wrap'>
            {canViewCrewDocumentFile && (
              <button
                type='button'
                className='btn btn-sm btn-icon btn-info'
                title='Preview'
                onClick={() => handlePreview(index)}
              >
                <i className='bi bi-eye'></i>
              </button>
            )}
            {hasNewFile && canUploadCrewDocument && (
              <button
                type='button'
                className='btn btn-sm btn-primary'
                title='Update file'
                onClick={() => handleSaveRow(index)}
              >
                Update File
              </button>
            )}
            {canDeleteCrewDocument && (
              <button
                type='button'
                className='btn btn-sm btn-icon btn-secondary'
                title='Remove'
                onClick={() => handleRemoveFile(index)}
              >
                <i className='bi bi-trash'></i>
              </button>
            )}
            {!canViewCrewDocumentFile && !canDeleteCrewDocument && (
              <span className='text-success small'>Uploaded</span>
            )}
          </div>
        )
      }

      if (!canUploadCrewDocument) {
        return <span className='text-muted small'>Not uploaded</span>
      }

      const fileInputId = `flag-file-input-${index}`
      const pendingFileName = row.file?.name || ''

      return (
        <div className='d-flex align-items-center gap-2 flex-wrap'>
          <input
            id={fileInputId}
            type='file'
            className='d-none'
            accept='.pdf,.png,.jpg,.jpeg'
            onChange={(e) => handleFileChange(index, e.target.files?.[0] || null)}
          />
          {!pendingFileName ? (
            <label
              htmlFor={fileInputId}
              className='btn btn-light-primary mb-0'
              style={{
                height: '31px',
                minWidth: '102px',
                padding: '0 0.75rem',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.875rem',
                whiteSpace: 'nowrap',
                lineHeight: 1,
                cursor: 'pointer',
              }}
            >
              Choose File
            </label>
          ) : (
            <label
              htmlFor={fileInputId}
              className='text-primary small mb-0 text-truncate'
              style={{maxWidth: '160px', cursor: 'pointer'}}
              title={pendingFileName}
            >
              {pendingFileName}
            </label>
          )}
          {hasNewFile && (
            <button
              type='button'
              className='btn btn-primary'
              style={{
                height: '31px',
                minWidth: '78px',
                padding: '0 0.75rem',
                fontSize: '0.875rem',
                whiteSpace: 'nowrap',
                lineHeight: 1,
              }}
              onClick={() => handleSaveRow(index)}
            >
              Upload
            </button>
          )}
        </div>
      )
    }

    useEffect(() => {
      if (!existingFlagDocuments?.length) return

      const foundFlags = Array.from(
        new Set(existingFlagDocuments.map((d: any) => d.flag).filter(Boolean))
      )
      const allFlags = Array.from(new Set([...DEFAULT_FLAGS, ...foundFlags]))
      setActiveFlags(allFlags)

      const byKey = new Map(
        existingFlagDocuments.map((d: CrewFlagDocument) => [`${d.flag}_${d.docType}`, d])
      )

      setRows((prev) => {
        const rowsMap = new Map(prev.map((r) => [`${r.flag}_${r.docType}`, r]))
        const nextRows: FlagRow[] = []

        for (const flag of allFlags) {
          for (const docType of DOC_TYPES) {
            const key = `${flag}_${docType}`
            const existingRow = rowsMap.get(key)
            const found = byKey.get(key)

            if (found) {
              nextRows.push({
                flag,
                docType,
                id: found.id,
                documentNumber: found.documentNumber ?? '',
                issueDate: parseDateToString(found.issueDate),
                expiryDate: parseDateToString(found.expiryDate),
                filePath: found.filePath ?? null,
                fileName: found.fileName ?? null,
                file: null,
              })
            } else if (existingRow) {
              nextRows.push(existingRow)
            } else {
              nextRows.push({
                flag,
                docType,
                documentNumber: '',
                issueDate: '',
                expiryDate: '',
                id: null,
                filePath: null,
                fileName: null,
                file: null,
              })
            }
          }
        }

        setInitialRows(nextRows)
        return nextRows
      })
    }, [existingFlagDocuments])

    return (
      <>
        <div className='col-12 mb-4'>
          <h6 className='mb-3 fw-bold'>Flag Documents</h6>
          <div className='table-responsive'>
            <table className='table table-bordered'>
              <thead className='table-light'>
                <tr>
                  <th style={{width: '20%'}}>Flag / Document</th>
                  <th style={{width: '20%'}}>Document Number</th>
                  <th style={{width: '20%'}}>Issue Date</th>
                  <th style={{width: '20%'}}>Expiry Date</th>
                  <th style={{width: '20%'}}>File Upload</th>
                </tr>
              </thead>
              <tbody>
                {activeFlags.map((flag) => (
                  <React.Fragment key={flag}>
                    <tr
                      className='table-secondary align-middle'
                      style={{borderTop: '2px solid #ccc'}}
                    >
                      <td colSpan={5} className='py-2 bg-light-warning'>
                        <div className='d-flex align-items-center justify-content-between px-2'>
                          <span
                            className='fw-bold text-uppercase badge badge-secondary badge-lg my-2'
                            style={{letterSpacing: '0.05em', fontSize: '13px'}}
                          >
                            {getFlagLabel(flag)}
                          </span>
                          {!DEFAULT_FLAGS.includes(flag) && (
                            <button
                              type='button'
                              className='btn btn-sm btn-icon btn-light-danger'
                              title={`Remove ${getFlagLabel(flag)} Flag`}
                              onClick={() => handleRemoveFlag(flag)}
                            >
                              <i className='bi bi-trash3'></i>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {DOC_TYPES.map((docType) => {
                      const idx = rows.findIndex((r) => r.flag === flag && r.docType === docType)
                      if (idx < 0) return null
                      const row = rows[idx]
                      return (
                        <tr key={`${flag}-${docType}`}>
                          <td>
                            <strong>{DOC_LABELS[docType]}</strong>
                          </td>
                          <td>
                            <input
                              type='text'
                              className='form-control form-control-sm'
                              value={row.documentNumber}
                              onChange={(e) =>
                                handleFieldChange(idx, 'documentNumber', e.target.value)
                              }
                            />
                          </td>
                          <td>
                            <input
                              type='date'
                              className='form-control form-control-sm'
                              value={row.issueDate}
                              onChange={(e) => handleFieldChange(idx, 'issueDate', e.target.value)}
                            />
                          </td>
                          <td>
                            <input
                              type='date'
                              className='form-control form-control-sm'
                              value={row.expiryDate}
                              onChange={(e) => handleFieldChange(idx, 'expiryDate', e.target.value)}
                            />
                          </td>
                          <td>{renderFileCell(idx)}</td>
                        </tr>
                      )
                    })}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* Bottom Add Flag Control Bar */}
          <div className='card bg-light-primary border border-primary border-dashed p-4 mt-3'>
            <div className='d-flex flex-wrap align-items-center justify-content-between gap-3'>
              <div>
                <h6 className='fw-bold text-gray-800 mb-1'>Add Additional Flag Documents</h6>
                <div className='text-muted fs-7'>
                  Select a maritime flag or enter a custom flag name to automatically generate the 7 standard document upload fields.
                </div>
              </div>
              <div className='d-flex align-items-center gap-2 flex-wrap'>
                {!showCustomInput ? (
                  <select
                    className='form-select form-select-sm form-select-solid'
                    style={{minWidth: '220px'}}
                    value={selectedNewFlag}
                    onChange={(e) => {
                      if (e.target.value === '__CUSTOM__') {
                        setShowCustomInput(true)
                        setSelectedNewFlag('')
                      } else {
                        setSelectedNewFlag(e.target.value)
                      }
                    }}
                  >
                    <option value=''>-- Select Flag Country --</option>
                    {POPULAR_FLAGS.filter((f) => !activeFlags.includes(f.code)).map((f) => (
                      <option key={f.code} value={f.code}>
                        {f.label}
                      </option>
                    ))}
                    <option value='__CUSTOM__'>+ Other (Custom Flag Name)...</option>
                  </select>
                ) : (
                  <div className='d-flex align-items-center gap-1'>
                    <input
                      type='text'
                      className='form-control form-control-sm'
                      placeholder='Enter Flag Name (e.g. PANAMA)'
                      value={customFlagInput}
                      onChange={(e) => setCustomFlagInput(e.target.value)}
                      style={{minWidth: '200px'}}
                    />
                    <button
                      type='button'
                      className='btn btn-sm btn-icon btn-light'
                      title='Back to list'
                      onClick={() => {
                        setShowCustomInput(false)
                        setCustomFlagInput('')
                      }}
                    >
                      <i className='bi bi-x'></i>
                    </button>
                  </div>
                )}
                <button
                  type='button'
                  className='btn btn-sm btn-primary d-inline-flex align-items-center gap-1'
                  disabled={
                    (!showCustomInput && !selectedNewFlag) ||
                    (showCustomInput && !customFlagInput.trim())
                  }
                  onClick={() => handleAddFlag()}
                >
                  <i className='bi bi-plus-lg fs-6'></i>
                  <span>Add Flag</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {viewer.open && (
          <div className='modal fade show' style={{display: 'block'}} onClick={closeViewer}>
            <div className='modal-dialog modal-xl' onClick={(e) => e.stopPropagation()}>
              <div className='modal-content'>
                <div className='modal-header'>
                  <h5 className='modal-title m-0'>{viewer.title}</h5>
                  <button type='button' className='btn-close' onClick={closeViewer} />
                </div>
                <div className='modal-body' style={{height: '80vh'}}>
                  {viewer.mime?.startsWith('image/') ? (
                    <img
                      src={viewer.href ?? ''}
                      alt=''
                      style={{maxWidth: '100%', maxHeight: '100%'}}
                    />
                  ) : (
                    <iframe
                      src={viewer.href ?? ''}
                      width='100%'
                      height='100%'
                      title='Document preview'
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    )
  }
)

export default FlagDocuments
